import React from 'react'
import moment from 'moment'
import { FaExclamationTriangle } from 'react-icons/fa'
import { Modal, ModalBody, ModalHeader, Table } from 'reactstrap'
import {
  TRIVIA_USER_IDENTIFICATION_OPTIONS,
  SENTIMENTS_TYPO_MAP,
} from '../data'
import { profilePicValidityCheck } from '../../../utils/constants'

// NOTE: This modal INTENTIONALLY overrides the
// `allowAnonymousResponses && responsesData.length <= 5` gate used on the
// By User tab (TriviaResultsResponses.jsx:1017-1022). Per the ticket,
// rows here must surface real user identities even when that gate would
// otherwise coerce names to "Anonymous user", but per-row
// `result.isAnonymous` is still respected.

const getInitials = (user) => {
  const firstName = user && user.firstName && user.firstName.length > 0 ? user.firstName : 'N'
  const lastName = user && user.lastName && user.lastName.length > 0 ? user.lastName : 'N'

  if (firstName === 'N' && lastName === 'N') {
    if (user && user.email) {
      return user.email.charAt(0) + user.email.split('@')[1].charAt(0)
    }
    return ''
  }
  return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()
}

// Keep in sync with the helper of the same name in
// TriviaResultsResponses.jsx (currently a private inner helper there —
// not exported). If behavior changes there, update here as well.
const constructUserObjForExternalUser = (obj) => {
  if (!obj) return null
  if (obj.email) {
    return {
      firstName: obj.email,
      identification: '(email)',
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.name) {
    const arr = obj.name.split(' ')
    return {
      identification: '(name)',
      firstName: arr[0],
      lastName: arr[1] || '',
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.mobileNumber) {
    return {
      identification: '(phone)',
      firstName: obj.mobileNumber,
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.firstName || obj.lastName) {
    return {
      identification: '(name)',
      firstName: obj.firstName || '',
      lastName: obj.lastName || '',
      email: obj.email,
      mobileNumber: obj.mobileNumber,
    }
  }
  if (obj.employeeId) {
    return {
      identification: '(employeeId)',
      firstName: obj.employeeId,
      lastName: obj.lastName || '',
      email: '',
      mobileNumber: '',
      employeeId: obj.employeeId,
    }
  }
  return {
    identification: 'anonymous',
    firstName: obj.firstName || '',
    lastName: obj.lastName || '',
    email: obj.email,
    mobileNumber: obj.mobileNumber,
    employeeId: obj.employeeId,
  }
}

// Per-row identity rendering for the Needs Attention modal.
// IMPORTANT: this helper ONLY checks the per-row `isAnonymousFlag` —
// it does NOT apply the `allowAnonymousResponses && length <= 5` rule
// that getIdentityDisplay() uses on the By User tab. That is the deliberate
// override required by the ticket.
const getIdentityDisplay = (user, isAnonymousFlag, currentTrivvy) => {
  if (!user || isAnonymousFlag) {
    return 'Anonymous user'
  }

  let pref = ''
  if (currentTrivvy && currentTrivvy.userIdentificationOptions) {
    pref = Array.isArray(currentTrivvy.userIdentificationOptions)
      ? currentTrivvy.userIdentificationOptions[0]
      : currentTrivvy.userIdentificationOptions
  }

  let field = ''
  let val = ''
  for (let i = 0; i < TRIVIA_USER_IDENTIFICATION_OPTIONS.length; i++) {
    const opt = TRIVIA_USER_IDENTIFICATION_OPTIONS[i]
    if (opt.value === pref) {
      field = opt.field || 'email'
      val = field ? user[field] : ''
      if (opt.value === 'FIRST_LAST_NAME') {
        val = (user.firstName ? user.firstName : '') + ' ' + (user.lastName ? user.lastName : '')
      }
      break
    }
  }
  val = val != null ? String(val).trim() : ''
  if (!val) {
    return 'Anonymous user'
  }
  return val
}

const hasIdentityValue = (obj) => {
  if (!obj || typeof obj !== 'object') return false
  const keys = ['email', 'employeeId', 'mobileNumber', 'firstName', 'lastName']
  for (let i = 0; i < keys.length; i++) {
    const v = obj[keys[i]]
    if (v !== null && v !== undefined && String(v).trim() !== '') {
      return true
    }
  }
  return false
}

const NeedsAttentionModal = ({ open, onClose, submissions, currentTrivvy, onRowClick }) => {
  const flagged = Array.isArray(submissions)
    ? submissions.filter((s) => s && s.needsAttention === true)
    : []

  // Build row objects that mirror the shape produced in
  // TriviaResultsResponses.jsx:444-509 (the By User row mapping).
  const rows = flagged.map((result) => {
    const user = result.userId
      ? result.userId
      : result.userData
        ? constructUserObjForExternalUser(result.userData)
        : null
    return {
      _id: result._id,
      user,
      isAnonymous: result.isAnonymous,
      needsAttention: result.needsAttention,
      // Carry the raw result onto the row so the parent's MyTriviaDetails
      // can be opened without a second lookup. We inline the modalData
      // shape verbatim below (matches the canonical builder at
      // TriviaResultsResponses.jsx:1105-1127).
      _raw: result,
    }
  })

  const handleRowClick = (row) => {
    if (!row || !row._raw) return
    const resObj = row._raw
    // Modal data shape mirrors openSingleResultModal at
    // TriviaResultsResponses.jsx:1105-1127.
    const modalData = {
      resultId: resObj._id,
      isAnonymous: resObj.isAnonymous,
      quizResponses: resObj.responses,
      triviaId: resObj.triviaId,
      percentCorrect: resObj.percentCorrect,
      sourceLink: currentTrivvy ? currentTrivvy.uploadUrl : null,
      triviaName: currentTrivvy ? currentTrivvy.name : '',
      quizSubmittedAt: resObj.createdOn,
      totalTime: resObj.totalTime,
      completion: resObj.completion,
      rating: resObj.rating,
      ratingComment: resObj.ratingComment,
      userResultObj: row.user,
      triviaCategoryType: currentTrivvy ? currentTrivvy.triviaCategoryType : null,
    }
    onRowClick(modalData)
  }

  const renderIdentityCell = (row) => {
    const showRealIdentity = hasIdentityValue(row.user) && !row.isAnonymous
    if (showRealIdentity) {
      return (
        <span className="trivia_table_text_wrapper">
          {profilePicValidityCheck(row.user.profilePic) ? (
            <img
              src={row.user.profilePic}
              height="35px"
              width="35px"
              className="rounded-circle"
              alt=""
            />
          ) : (
            <span
              className="spotlightUserInitials"
              data-letters={getInitials(row.user)}
            ></span>
          )}{' '}
          {(row.user.firstName ? row.user.firstName : '') +
            ' ' +
            (row.user.lastName ? row.user.lastName : '') +
            ' ' +
            ((row.user.firstName === '' && row.user.lastName === '') ||
              (!row.user.firstName && !row.user.lastName)
              ? getIdentityDisplay(row.user, row.isAnonymous, currentTrivvy)
              : '')}
        </span>
      )
    }
    return (
      <span className="trivia_table_text_wrapper">
        <span className="spotlightUserInitials" data-letters={'AU'}></span>
        Anonymous user
      </span>
    )
  }

  return (
    <Modal isOpen={open} toggle={onClose} size="lg">
      <ModalHeader className="trivia_modal_header_bg" toggle={onClose}>
        Users that need attention
      </ModalHeader>
      <ModalBody>
        <div className="mb-3 small text-muted">
          The list below shows responses flagged as needing attention. Click a row to review the full submission.
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-4 text-muted">
            No users currently need attention.
          </div>
        ) : (
          <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
            <Table hover responsive className="mb-0">
              <thead style={{ fontSize: '14px' }}>
                <tr>
                  {/* <th>User</th> */}
                  <th className="text-center">Response Date</th>
                  <th className="text-center">Sentiment</th>
                  <th className="text-center">Needs Attention</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // Field mapping matches TriviaResultsResponses.jsx:464 —
                  // raw doc uses `isSubmittedDate` for the response date.
                  const raw = row._raw || {}
                  const respDate = raw.isSubmittedDate
                  const sentimentKey =
                    raw.sentimentsScore && raw.sentimentsScore.sentiment
                      ? raw.sentimentsScore.sentiment
                      : null
                  const sentimentLabel = sentimentKey
                    ? SENTIMENTS_TYPO_MAP[sentimentKey] || '—'
                    : '—'
                  return (
                    <tr
                      key={row._id}
                      className="cursor-pointer"
                      onClick={() => handleRowClick(row)}
                    >
                      {/* <td>{renderIdentityCell(row)}</td> */}
                      <td className="text-center">
                        <span className="trivia_table_text_wrapper">
                          {respDate ? (
                            <>
                              {moment(respDate).format('ll')}
                              <br />
                              {moment(respDate).format('LT')}
                            </>
                          ) : (
                            '—'
                          )}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="trivia_table_text_wrapper">
                          {sentimentLabel}
                        </span>
                      </td>
                      <td className="text-center">
                        {row.needsAttention ? (
                          <FaExclamationTriangle color="goldenrod" size="1.8em" />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default NeedsAttentionModal
